import { ButtonGradientPrimary, Icon, ModalDialog } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ObjectivePillar } from '@/types/objectives';

import PillarIcon from '@/components/objectives/PillarIcon';
import { derivePillarTileGradient } from '@/components/objectives/pillarSurface';
import { useStrategicVision } from '@/hooks/objectives/useStrategicVision';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';

interface PillarVisionModalProps {
    isOpen: boolean;
    onClose: () => void;
    pillar: ObjectivePillar;
}

/**
 * "Notre vision partagée": the window a pillar click opens, instead of navigating to the pillar
 * page. The library's titled dialog gives it its name, its close button, Escape and the focus
 * trap; the pillar's own tile and name head it, the company vision and the pillar description
 * follow.
 */
export default function PillarVisionModal({ isOpen, onClose, pillar }: PillarVisionModalProps) {
    const { t } = useTranslation();
    const strategicVision = useStrategicVision();
    const pillarDescription = getLocalizedText(pillar.description ?? undefined);

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={getLocalizedText(pillar.name)}
            headingLevel={2}
            titleIcon={
                <span
                    className={
                        'flex h-[65px] w-[65px] flex-shrink-0 items-center justify-center rounded-[10px] shadow-sm'
                    }
                    style={{
                        background: derivePillarTileGradient(
                            resolveCompanyValueIconBackground(pillar.icon, pillar.iconBackground),
                        ),
                    }}
                    aria-hidden
                >
                    <PillarIcon
                        icon={pillar.icon}
                        size={40}
                    />
                </span>
            }
            subtitle={t('objectives.detail.pillar')}
            className={'max-w-[678px] p-[39px_47px_45px_47px]'}
            padding={'none'}
            radius={'2xl'}
            shadow={'lg'}
            bordered
            backdropBlur
            dataTestId={'pillar-vision-modal'}
            footer={
                <div className={'flex justify-end'}>
                    <ButtonGradientPrimary
                        onClick={onClose}
                        size={'sm'}
                        dataTestId={'pillar-vision-modal-understood'}
                    >
                        {t('objectives.detail.pillarModal.understood')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-[48px]'}>
                {strategicVision && (
                    <div
                        className={
                            'bg-obj-vision-tint rounded-[16px] border border-white p-[31px_42px] backdrop-blur-[2px]'
                        }
                        data-testid={'pillar-vision-modal-shared-vision'}
                    >
                        <div className={'flex items-start gap-6'}>
                            <Icon
                                name={'cryptocompany-target'}
                                size={32}
                                colorClassName={'text-obj-accent'}
                                strokeWidth={2.4}
                            />

                            <div className={'flex min-w-0 flex-col gap-1.5'}>
                                <h3 className={'text-navy text-[20px] font-medium'}>
                                    {t('objectives.detail.pillarModal.sharedVision')}
                                </h3>
                                {/* The window's one typographic accent: the vision is quoted,
                                    not reported -- the quotation marks come from the locale so
                                    each language keeps its own (« » in French, “ ” in English). */}
                                <p
                                    className={
                                        'text-obj-purple-deep text-[14px] leading-relaxed font-bold'
                                    }
                                >
                                    {t('objectives.detail.pillarModal.visionQuote', {
                                        vision: strategicVision,
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {pillarDescription && (
                    <div
                        className={'flex flex-col gap-6'}
                        data-testid={'pillar-vision-modal-pillar-description'}
                    >
                        <h3 className={'text-navy text-[20px] font-medium'}>
                            {t('objectives.detail.pillarModal.thisPillar')}
                        </h3>
                        <p className={'text-obj-muted text-[14px] font-medium'}>
                            {pillarDescription}
                        </p>
                    </div>
                )}
            </div>
        </ModalDialog>
    );
}
